// src/pages/CreditsPage.jsx -- Role-Distinct Credit Management (v3)
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/useAuth';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter.js';
import { useTenantExportBranding } from '../hooks/useTenantExportBranding.js';
import {
  getMyWallet, getMyUsage, getMyTransactions, getUsageBreakdown, exportUsageCSV,
  adminGetAllWallets, adminGetPartners,
  getPartnerCredits, partnerTopUpPool, partnerAllocateCredits,
  getAllocationPlanDefaults, createAllocation, listAllocations, toggleAllocation,
  getAdminUsageOverview, updateWalletConfig, partnerCreateAllocation,
  partnerListAllocations, partnerGetUsageOverview, getMyAllocation, getMyAllocationHistory,
  updateMyAlertSettings, getMyUsageFiltered, updateAllocation,
  partnerGetJobUsage, getPartnerPoolConfig, updatePartnerPoolConfig, deleteAllocation
} from '../api/credits';
import { PageHeader, LoadingSpinner, StandardPageLayout } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { formatDateTime } from '../utils/dateTime';

const MOD_CLR = { DATA: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800', ML: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800', GENAI: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800', WORKFLOW: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800', WORKLIST: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800' };
const MOD_BAR = { DATA: 'bg-indigo-500', ML: 'bg-amber-500', GENAI: 'bg-emerald-500', WORKFLOW: 'bg-violet-500', WORKLIST: 'bg-teal-500' };
const STS_CLR = { completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', running: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400', failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' };
const fmt = (n, d = 0) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtD = d => d ? formatDateTime(d) : ' -- ';
const today = () => new Date().toISOString().slice(0, 10);
const plus30 = s => { const d = new Date(s || today()); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); };

const PaginationControls = ({ page, total, limit, setPage }) => {
  const totalPages = Math.ceil(total / limit) || 1;
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/50">
      <span className="text-xs text-gray-500 font-medium">Showing {Math.min((page - 1) * limit + 1, total)} to {Math.min(page * limit, total)} of {total}</span>
      <div className="flex items-center gap-1.5">
        <button disabled={page <= 1} onClick={() => setPage(1)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors shadow-sm text-gray-700">First</button>
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors shadow-sm text-gray-700">Prev</button>
        <span className="text-xs font-bold px-2 text-gray-600">Page {page} of {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors shadow-sm text-gray-700">Next</button>
        <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors shadow-sm text-gray-700">Last</button>
      </div>
    </div>
  );
};

/** Alert threshold slider — tick labels use the same linear mapping as the native range thumb (min–max). */
const CREDIT_ALERT_SLIDER_MIN = 10;
const CREDIT_ALERT_SLIDER_MAX = 100;
/** Reference marks: min, mid, early warning (~70%), common cap (80%), high usage (90%), max */
const CREDIT_ALERT_SLIDER_TICKS = [10, 50, 70, 80, 90, 100];
const creditAlertValueToTrackPct = (v) =>
  ((v - CREDIT_ALERT_SLIDER_MIN) / (CREDIT_ALERT_SLIDER_MAX - CREDIT_ALERT_SLIDER_MIN)) * 100;

// " --  -- Cr Primitives " --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  -- Cr
const Badge = ({ label, color = 'bg-gray-100 text-gray-600', dot }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>
    {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}{label}
  </span>
);

const AllocationStatusBadge = ({ alloc }) => {
  if (!alloc) return null;
  const isExpired = alloc.expiry_date && new Date(alloc.expiry_date).getTime() < Date.now();
  if (isExpired) {
    return <Badge label="Expired" dot="bg-orange-400" color="bg-orange-50 text-orange-700" />;
  }
  if (!alloc.is_active) {
    return <Badge label="Disabled" dot="bg-gray-400" color="bg-gray-100 text-gray-500" />;
  }
  return <Badge label="Active" dot="bg-emerald-400" color="bg-emerald-50 text-emerald-700" />;
};

const Pill = ({ label, active, onClick }) => (
  <button onClick={onClick}
    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${active ? 'bg-white dark:bg-gray-700 shadow text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-100 dark:ring-indigo-800' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>
    {label}
  </button>
);

function Arc({ pct = 0, size = 140, stroke = 12 }) {
  const r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  const safe = Math.min(100, Math.max(0, pct));
  const color = safe >= 90 ? '#ef4444' : safe >= 70 ? '#f59e0b' : '#6366f1';
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={circ * (1 - safe / 100)} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset .6s ease' }} />
    </svg>
  );
}

function LinearBar({ pct = 0, color = 'bg-indigo-500', height = 'h-2' }) {
  const bg = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-400' : color;
  return (
    <div className={`${height} bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden`}>
      <div className={`${bg} h-full rounded-full transition-all duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

// " --  -- Cr Allocation Drawer (shared) " --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  --  -- Cr
function AllocationDrawer({ token, tenants = [], partnerId = null, poolAvailable = null, editItem = null, onClose, onSuccess }) {
  const s0 = today();
  const [form, setForm] = useState({
    tenant_id: editItem ? editItem.tenant_id : '',
    credits: editItem ? String(editItem.credits || '') : '',
    invoice_ref: editItem?.invoice_ref || '',
    start_date: editItem?.start_date ? editItem.start_date.slice(0, 10) : s0,
    expiry_date: editItem?.expiry_date ? editItem.expiry_date.slice(0, 10) : plus30(s0),
    is_recurring: !!editItem?.is_recurring,
    recurring_quota: editItem?.recurring_quota ? String(editItem.recurring_quota) : '',
    carryover_enabled: !!editItem?.carryover_enabled,
    overdraft_enabled: !!editItem?.overdraft_enabled,
    note: editItem?.note || ''
  });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!form.tenant_id) { setPlan(null); return; }
    getAllocationPlanDefaults(token, form.tenant_id)
      .then(d => { setPlan(d); if (!form.credits) setForm(f => ({ ...f, credits: String(d.plan_monthly_credits || '') })); })
      .catch(() => setPlan(null));
  }, [form.tenant_id]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const isOverride = plan && Number(form.credits) !== plan.plan_monthly_credits;

  const submit = async e => {
    e.preventDefault();
    const cr = Number(form.credits);
    if (!form.tenant_id) return setErr('Select a tenant');
    if (!cr || cr <= 0) return setErr('Credits must be > 0');
    if (poolAvailable !== null && cr > poolAvailable) return setErr(`Exceeds pool (${fmt(poolAvailable)} available)`);
    setBusy(true); setErr(null);
    try {
      const payload = {
        tenant_id: form.tenant_id, credits: cr,
        invoice_ref: form.invoice_ref || null,
        start_date: form.start_date, expiry_date: form.expiry_date,
        is_recurring: form.is_recurring,
        recurring_quota: form.is_recurring ? Number(form.recurring_quota || cr) : null,
        carryover_enabled: form.is_recurring ? form.carryover_enabled : false,
        overdraft_enabled: form.overdraft_enabled, note: form.note || null,
      };
      if (editItem) {
        await updateAllocation(token, editItem.id, payload);
      } else {
        await (partnerId ? partnerCreateAllocation : createAllocation)(token, payload);
      }
      onSuccess();
    } catch (ex) { setErr(ex.message || 'Failed'); }
    finally { setBusy(false); }
  };

  const inp = "w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-700";
  const tog = (on, onClick, label, sub) => (
    <label className="flex items-center justify-between cursor-pointer">
      <div><p className="text-sm font-medium text-gray-800 dark:text-gray-200">{label}</p>{sub && <p className="text-xs text-gray-400 dark:text-gray-500">{sub}</p>}</div>
      <div className={`w-10 h-6 rounded-full relative transition-colors ${on ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'}`} onClick={onClick}>
        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${on ? 'left-5' : 'left-1'}`} />
      </div>
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-lg bg-white dark:bg-gray-800 h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-indigo-600 to-violet-600">
          <div><h3 className="font-bold text-white text-lg">{editItem ? 'Edit Credit Allocation' : 'New Credit Allocation'}</h3>
            <p className="text-indigo-200 text-xs mt-0.5">{partnerId ? (editItem ? 'Modify tenant allocation' : 'Drawn from your partner pool') : (editItem ? 'Modify platform allocation' : 'Direct platform allocation')}</p></div>
          <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white"><DynamicIcon name="X" className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg flex items-center gap-2"><DynamicIcon name="AlertCircle" className="w-4 h-4" />{err}</div>}

          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Tenant *</label>
            <select className={inp} value={form.tenant_id} onChange={set('tenant_id')} required disabled={!!editItem}>
              <option value="">Choose tenant...</option>
              {tenants.map(t => <option key={t.tenant_id || t.id} value={t.tenant_id || t.id}>{t.tenant_name || t.name}</option>)}
            </select>
          </div>

          {plan && (
            <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-3">
              <DynamicIcon name="Layers" className="w-4 h-4 text-indigo-500" />
              <div className="flex-1 text-sm">
                <span className="font-bold text-indigo-700">{plan.plan_name}</span>
                <span className="text-indigo-400 ml-2">. Plan default: {fmt(plan.plan_monthly_credits)} -/mo</span>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Credits to Allocate *</label>
            <input type="number" className={inp} value={form.credits} onChange={set('credits')} min="1" step="1" required />
            {isOverride && (
              <div className="mt-2 flex items-center gap-1.5 text-amber-600 text-xs font-medium bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <DynamicIcon name="AlertTriangle" className="w-3.5 h-3.5" />
                Overriding plan default ({fmt(plan.plan_monthly_credits)} -) -' {fmt(Number(form.credits))} -
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Invoice / PO Reference</label>
            <input className={inp} value={form.invoice_ref} onChange={set('invoice_ref')} placeholder="e.g. INV-2026-042" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Start Date</label>
              <input type="date" className={inp} value={form.start_date} onChange={set('start_date')} /></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Expiry Date</label>
              <input type="date" className={inp} value={form.expiry_date} onChange={set('expiry_date')} /></div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-600">
            {tog(form.is_recurring, () => setForm(f => ({ ...f, is_recurring: !f.is_recurring })), 'Recurring Monthly', 'Auto-renews 30 days after expiry')}
            {form.is_recurring && (
              <>
                <div className="mb-3"><label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Monthly Renewal Quota</label>
                  <input type="number" className={inp} value={form.recurring_quota} onChange={set('recurring_quota')} placeholder={form.credits || 'Same as above'} /></div>
                {tog(form.carryover_enabled, () => setForm(f => ({ ...f, carryover_enabled: !f.carryover_enabled })), 'Credit Carryover', 'Unused credits carry over to next month')}
              </>
            )}
            {!partnerId && tog(form.overdraft_enabled, () => setForm(f => ({ ...f, overdraft_enabled: !f.overdraft_enabled })), 'Enable Overdraft', 'Allow tenant to use credits below zero')}
          </div>

          <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Notes</label>
            <textarea className={inp} rows={2} value={form.note} onChange={set('note')} placeholder="Optional internal note..." /></div>
        </form>
        <div className="px-6 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-5 py-2 text-sm rounded-lg bg-indigo-600 text-white font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
            {busy ? <DynamicIcon name="Loader2" className="w-4 h-4 animate-spin" /> : <DynamicIcon name="Coins" className="w-4 h-4" />}{editItem ? 'Save Changes' : 'Allocate Credits'}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------------
// PLATFORM ADMIN -- Invoice-linked allocation command center
// -------------------------------------------------------------------------------

// Invoice-linked allocation drawer for Platform Admin
function AdminAllocationDrawer({ token, tenants, partners, editItem = null, onClose, onSuccess }) {
  const s0 = today();
  const [targetType, setTT] = useState(editItem ? (editItem.tenant_id ? 'tenant' : 'partner') : 'tenant'); // 'tenant' | 'partner'
  const [form, setForm] = useState({
    target_id: editItem ? (editItem.tenant_id || editItem.partner_id) : '',
    credits: editItem ? String(editItem.credits || '') : '',
    invoice_ref: editItem?.invoice_ref || '',
    invoice_date: editItem?.invoice_date || '',
    start_date: editItem?.start_date ? editItem.start_date.slice(0, 10) : s0,
    expiry_date: editItem?.expiry_date ? editItem.expiry_date.slice(0, 10) : plus30(s0),
    is_recurring: !!editItem?.is_recurring,
    recurring_quota: editItem?.recurring_quota ? String(editItem.recurring_quota) : '',
    carryover_enabled: !!editItem?.carryover_enabled,
    overdraft_enabled: !!editItem?.overdraft_enabled,
    note: editItem?.note || ''
  });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (targetType !== 'tenant' || !form.target_id) { setPlan(null); return; }
    getAllocationPlanDefaults(token, form.target_id)
      .then(d => { setPlan(d); if (!form.credits) setForm(f => ({ ...f, credits: String(d.plan_monthly_credits || '') })); })
      .catch(() => setPlan(null));
  }, [form.target_id, targetType]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const isOverride = plan && Number(form.credits) !== plan.plan_monthly_credits;

  const submit = async e => {
    e.preventDefault();
    const cr = Number(form.credits);
    if (!form.target_id) return setErr('Select a target');
    if (!form.invoice_ref) return setErr('Invoice reference is required');
    if (!cr || cr <= 0) return setErr('Credits must be > 0');
    setBusy(true); setErr(null);
    try {
      const payload = {
        tenant_id: targetType === 'tenant' ? form.target_id : null,
        partner_id: targetType === 'partner' ? form.target_id : null,
        credits: cr, invoice_ref: form.invoice_ref,
        invoice_date: form.invoice_date || null,
        start_date: form.start_date, expiry_date: form.expiry_date,
        is_recurring: form.is_recurring,
        recurring_quota: form.is_recurring ? Number(form.recurring_quota || cr) : null,
        carryover_enabled: form.is_recurring ? form.carryover_enabled : false,
        overdraft_enabled: form.overdraft_enabled, note: form.note || null,
      };
      if (editItem) {
        await updateAllocation(token, editItem.id, payload);
      } else {
        await createAllocation(token, payload);
      }
      onSuccess();
    } catch (ex) { setErr(ex.message || 'Failed'); }
    finally { setBusy(false); }
  };

  const inp = "w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-700";
  const tog = (on, fn, lbl, sub) => (
    <label className="flex items-center justify-between cursor-pointer py-1">
      <div><p className="text-sm font-medium text-gray-800 dark:text-gray-200">{lbl}</p>{sub && <p className="text-xs text-gray-400 dark:text-gray-500">{sub}</p>}</div>
      <div className={`w-10 h-6 rounded-full relative transition-colors ${on ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'}`} onClick={fn}>
        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${on ? 'left-5' : 'left-1'}`} />
      </div>
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-lg bg-white dark:bg-gray-800 h-full shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-slate-800 to-indigo-700 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-white text-lg flex items-center gap-2">
              <DynamicIcon name="FileText" className="w-5 h-5 text-indigo-300" />{editItem ? 'Edit Allocation' : 'Invoice Credit Allocation'}
            </h3>
            <p className="text-xs text-indigo-200 mt-0.5">Platform Admin . {editItem ? 'Modify existing credit allocation' : 'Credits tied to invoice'}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white"><DynamicIcon name="X" className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">{err}</div>}

          {/* Target type toggle */}
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 block">Allocate To</label>
            <div className="flex gap-2">
              {[{ id: 'tenant', label: 'Tenant', icon: 'Building2' }, { id: 'partner', label: 'Partner', icon: 'Briefcase' }].map(t => (
                <button type="button" key={t.id} onClick={() => { setTT(t.id); setForm(f => ({ ...f, target_id: '', credits: '' })); setPlan(null); }}
                  disabled={!!editItem}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-bold transition-all
          ${targetType === t.id ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'} ${editItem ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <DynamicIcon name={t.icon} className="w-4 h-4" />{t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Target selector */}
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">
              {targetType === 'tenant' ? 'Select Tenant' : 'Select Partner'} *
            </label>
            <select className={inp} value={form.target_id} onChange={set('target_id')} required disabled={!!editItem}>
              <option value="">Choose {targetType}...</option>
              {(targetType === 'tenant' ? tenants : partners).map(t => (
                <option key={t.id || t.tenant_id} value={t.id || t.tenant_id}>{t.name || t.tenant_name}</option>
              ))}
            </select>
          </div>

          {/* Plan default info */}
          {plan && (
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 flex items-center gap-3">
              <DynamicIcon name="Layers" className="w-4 h-4 text-indigo-500 shrink-0" />
              <div className="text-sm">
                <span className="font-bold text-indigo-700">{plan.plan_name}</span>
                <span className="text-indigo-400 ml-2">Default: {fmt(plan.plan_monthly_credits)} -/mo</span>
              </div>
            </div>
          )}

          {/* Invoice section */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Invoice Details</p>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Invoice Reference *</label>
              <input className={inp} value={form.invoice_ref} onChange={set('invoice_ref')} placeholder="e.g. INV-2026-042" required />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Invoice Date</label>
              <input type="date" className={inp} value={form.invoice_date} onChange={set('invoice_date')} />
            </div>
          </div>

          {/* Credits */}
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Credits *</label>
            <input type="number" className={inp} value={form.credits} onChange={set('credits')} min="1" step="1" required placeholder="e.g. 3000" />
            {isOverride && (
              <div className="mt-2 flex items-center gap-1.5 text-amber-600 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <DynamicIcon name="AlertTriangle" className="w-3.5 h-3.5" />
                Overrides plan default ({fmt(plan.plan_monthly_credits)} -/mo)
              </div>
            )}
          </div>

          {/* Validity / Contract period */}
          {targetType === 'partner' ? (
            /* Partner allocation: contract window + monthly quota */
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-bold text-amber-600 uppercase tracking-wide flex items-center gap-1.5">
                <DynamicIcon name="CalendarClock" className="w-3.5 h-3.5" />Contract / Pool Settings
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Contract Start</label>
                  <input type="date" className={inp} value={form.start_date} onChange={set('start_date')} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Contract Expiry</label>
                  <input type="date" className={inp} value={form.expiry_date} onChange={set('expiry_date')}
                    min={form.start_date} />
                </div>
              </div>
              <p className="text-[11px] text-amber-500">Partner cannot allocate to tenants after the contract expiry date.</p>
              <div className="border-t border-amber-200 pt-3">
                {tog(form.is_recurring, () => setForm(f => ({ ...f, is_recurring: !f.is_recurring })), 'Monthly Recurring Credit', 'Credits replenish every 30 days within the contract window')}
                {form.is_recurring && (
                  <div className="mt-2 space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Monthly Quota (credits/month)</label>
                      <input type="number" className={inp} value={form.recurring_quota} onChange={set('recurring_quota')}
                        placeholder={form.credits || 'e.g. 10000'} />
                    </div>
                    {tog(form.carryover_enabled, () => setForm(f => ({ ...f, carryover_enabled: !f.carryover_enabled })), 'Credit Carryover', 'Unused credits carry over to next month')}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Tenant allocation: standard start/expiry */
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Start Date</label>
                <input type="date" className={inp} value={form.start_date} onChange={set('start_date')} /></div>
              <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Expiry Date</label>
                <input type="date" className={inp} value={form.expiry_date} onChange={set('expiry_date')} /></div>
            </div>
          )}

          {/* Options (tenant only) */}
          {targetType === 'tenant' && (
            <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600 rounded-xl p-4 space-y-3">
              {tog(form.is_recurring, () => setForm(f => ({ ...f, is_recurring: !f.is_recurring })), 'Recurring Monthly', 'Auto-renews every 30 days')}
              {form.is_recurring && (
                <div className="space-y-3">
                  <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block">Monthly Renewal Quota</label>
                    <input type="number" className={inp} value={form.recurring_quota} onChange={set('recurring_quota')} placeholder={form.credits || 'Same as above'} /></div>
                  {tog(form.carryover_enabled, () => setForm(f => ({ ...f, carryover_enabled: !f.carryover_enabled })), 'Credit Carryover', 'Unused credits carry over to next month')}
                </div>
              )}
              {tog(form.overdraft_enabled, () => setForm(f => ({ ...f, overdraft_enabled: !f.overdraft_enabled })), 'Allow Overdraft', 'Tenant can consume credits below zero')}
            </div>
          )}

          <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block">Internal Note</label>
            <textarea className={inp} rows={2} value={form.note} onChange={set('note')} placeholder="Optional..." /></div>
        </form>

        <div className="px-6 py-4 border-t dark:border-gray-700 bg-slate-50 dark:bg-gray-800/80 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={submit} disabled={busy}
            className="px-5 py-2 text-sm rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
            {busy ? <DynamicIcon name="Loader2" className="w-4 h-4 animate-spin" /> : <DynamicIcon name="FileText" className="w-4 h-4" />}
            {editItem ? 'Save Changes' : 'Create Allocation'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminCreditsView({ token }) {
  const [tab, setTab] = useState('allocations');
  const [allocs, setAllocs] = useState([]);
  const [overview, setOver] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [partners, setPartners] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoad] = useState(true);
  const [search, setSearch] = useState('');
  const [pFilter, setPF] = useState('');
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const [a, o, w, p] = await Promise.all([
        listAllocations(token), getAdminUsageOverview(token),
        adminGetAllWallets(token), adminGetPartners(token),
      ]);
      setAllocs(Array.isArray(a) ? a : []);
      setOver(Array.isArray(o) ? o : []);
      setWallets(Array.isArray(w) ? w : []);
      setPartners(Array.isArray(p) ? p : []);
    } catch (e) { console.error(e); } finally { setLoad(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const partnerList = useMemo(() => [...new Set(overview.map(r => r.partner_name).filter(Boolean))], [overview]);
  // Tenant list for allocation drawer: all tenants from usage overview (not just those with wallets)
  const tenantsForDrawer = useMemo(() => {
    const seen = new Set();
    return (overview || []).filter(r => { if (!r.tenant_id || seen.has(r.tenant_id)) return false; seen.add(r.tenant_id); return true; }).map(r => ({ id: r.tenant_id, tenant_id: r.tenant_id, name: r.tenant_name || r.tenant_id, tenant_name: r.tenant_name || r.tenant_id })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [overview]);
  // Partners list for allocation drawer: deduplicated by id
  const partnersForDrawer = useMemo(() => {
    const seen = new Set();
    return (partners || []).filter(p => { if (!p.id || seen.has(p.id)) return false; seen.add(p.id); return true; }).map(p => ({ id: p.id, name: p.name }));
  }, [partners]);
  const filtered = useMemo(() => overview.filter(r => {
    const ms = !search || r.tenant_name?.toLowerCase().includes(search.toLowerCase());
    const mp = !pFilter || r.partner_name === pFilter;
    return ms && mp;
  }), [overview, search, pFilter]);

  const totalC = allocs.filter(a => a.is_active).reduce((s, a) => s + (Number(a.credits) || 0), 0);
  const near = overview.filter(r => r.used_pct >= 80).length;
  const recur = allocs.filter(a => a.is_recurring && a.is_active).length;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Stat tiles -- slate command-center */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Credits Issued', value: `${fmt(totalC)} -`, icon: 'Coins', bg: 'from-indigo-600 to-indigo-800', sub: `${allocs.filter(a => a.is_active).length} active` },
          { label: 'Tenants', value: wallets.length, icon: 'Building2', bg: 'from-slate-700 to-slate-900', sub: `${partners.length} partners` },
          { label: 'Recurring Plans', value: recur, icon: 'RefreshCw', bg: 'from-violet-600 to-violet-800', sub: 'auto-renew monthly' },
          { label: 'Near Limit', value: near, icon: 'AlertTriangle', bg: near > 0 ? 'from-rose-500 to-rose-700' : 'from-emerald-500 to-emerald-700', sub: near > 0 ? '>=80% usage' : 'all healthy' },
        ].map(c => (
          <div key={c.label} className={`bg-gradient-to-br ${c.bg} rounded-2xl p-5 text-white shadow-lg`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-bold uppercase tracking-widest opacity-70">{c.label}</span>
              <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
                <DynamicIcon name={c.icon} className="w-4 h-4" />
              </div>
            </div>
            <p className="text-3xl font-black">{c.value}</p>
            <p className="text-xs opacity-60 mt-1">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          {[{ id: 'allocations', label: 'Invoice Allocations', icon: 'FileText' }, { id: 'usage', label: 'Usage Overview', icon: 'BarChart3' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold transition-all ${tab === t.id ? 'bg-white dark:bg-gray-700 shadow text-indigo-700 dark:text-indigo-300' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}>
              <DynamicIcon name={t.icon} className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>
        {tab === 'allocations' && (
          <button onClick={() => { setEditItem(null); setDrawer(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all">
            <DynamicIcon name="Plus" className="w-4 h-4" />New Invoice Allocation
          </button>
        )}
      </div>

      {flash && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3 rounded-xl flex justify-between"><span>'" {flash}</span><button onClick={() => setFlash(null)}>--</button></div>}

      {/* Allocations table */}
      {tab === 'allocations' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
            <p className="text-sm font-bold text-gray-700">All Credit Allocations</p>
            <p className="text-xs text-gray-400">{allocs.length} total . {allocs.filter(a => a.is_active).length} active</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>{['Date', 'Target', 'Type', 'Plan', 'Plan Quota', 'Allocated', 'Override', 'Invoice Ref', 'Inv Date', 'Period', 'Recurring', 'Overdraft', 'Status', ''].map(h =>
                  <th key={h} className="text-left text-[11px] font-bold text-slate-400 uppercase tracking-wide py-3 px-3 whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {allocs.length === 0 && <tr><td colSpan={14} className="text-center text-gray-300 py-16 text-sm">No allocations yet -- create one with an invoice reference</td></tr>}
                {allocs.map(r => (
                  <tr key={r.id} className="hover:bg-indigo-50/20 transition-colors group">
                    <td className="py-3 px-3 text-xs text-gray-400 whitespace-nowrap">{fmtD(r.created_at)}</td>
                    <td className="py-3 px-3 font-semibold text-gray-800 whitespace-nowrap">{r.tenant_name || r.partner_name || ' -- '}</td>
                    <td className="py-3 px-3">
                      <Badge label={r.partner_name && !r.tenant_name ? 'Partner' : 'Tenant'} color={r.partner_name && !r.tenant_name ? 'bg-sky-100 text-sky-700' : 'bg-indigo-50 text-indigo-700'} />
                    </td>
                    <td className="py-3 px-3"><Badge label={r.plan_name || ' -- '} color="bg-slate-100 text-slate-600" /></td>
                    <td className="py-3 px-3 font-mono text-xs text-gray-400">{fmt(r.plan_default_credits)} -</td>
                    <td className="py-3 px-3 font-bold text-gray-900 whitespace-nowrap">{fmt(r.credits)} -</td>
                    <td className="py-3 px-3">{r.is_override ? <Badge label="Custom" color="bg-amber-100 text-amber-700" /> : <span className="text-gray-200 text-xs"> -- </span>}</td>
                    <td className="py-3 px-3 font-mono text-xs font-bold text-indigo-600 whitespace-nowrap">{r.invoice_ref || <span className="text-gray-300 font-normal"> -- </span>}</td>
                    <td className="py-3 px-3 text-xs text-gray-400">{r.invoice_date ? fmtD(r.invoice_date) : ' -- '}</td>
                    <td className="py-3 px-3 text-xs text-gray-400 whitespace-nowrap">{fmtD(r.start_date)} -' {fmtD(r.expiry_date)}</td>
                    <td className="py-3 px-3">{r.is_recurring ? <Badge label=">> Monthly" color="bg-violet-100 text-violet-700" /> : <span className="text-gray-200 text-xs"> -- </span>}</td>
                    <td className="py-3 px-3">{r.overdraft_enabled ? <Badge label="On" color="bg-orange-50 text-orange-600" /> : <span className="text-gray-300 text-xs">Off</span>}</td>
                    <td className="py-3 px-3">
                      <AllocationStatusBadge alloc={r} />
                    </td>
                    <td className="py-3 px-3 flex items-center gap-2">
                      <button onClick={() => toggleAllocation(token, r.id, !r.is_active).then(load)}
                        className="text-xs text-indigo-500 hover:text-indigo-700 opacity-0 group-hover:opacity-100 whitespace-nowrap">
                        {r.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button onClick={() => { setEditItem(r); setDrawer(true); }}
                        className="text-xs text-indigo-500 hover:text-indigo-700 opacity-0 group-hover:opacity-100 whitespace-nowrap">
                        Edit
                      </button>
                      <button onClick={() => { if(window.confirm('Delete allocation? This will reverse the credits.')) deleteAllocation(token, r.id).then(load); }}
                        className="text-xs text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 whitespace-nowrap">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Usage overview */}
      {tab === 'usage' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <DynamicIcon name="Search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
              <input className="pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm w-56"
                placeholder="Search tenant..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-600"
              value={pFilter} onChange={e => setPF(e.target.value)}>
              <option value="">All Partners</option>
              {partnerList.map(p => <option key={p}>{p}</option>)}
            </select>
            <span className="text-xs text-gray-400 ml-auto">{filtered.length} tenants</span>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>{['Tenant', 'Partner', 'Plan', 'Plan Default', 'Allocated', 'Used', 'Available', 'Usage %', 'Reset', 'Flags'].map(h =>
                    <th key={h} className="text-left text-[11px] font-bold text-slate-400 uppercase tracking-wide py-3 px-4 whitespace-nowrap">{h}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.length === 0 && <tr><td colSpan={10} className="text-center text-gray-300 py-12">No data</td></tr>}
                  {filtered.map(r => {
                    const pct = r.used_pct || 0;
                    const pc = pct >= 90 ? 'text-red-600' : pct >= 70 ? 'text-amber-600' : 'text-emerald-600';
                    return (
                      <tr key={r.tenant_id} className="hover:bg-slate-50/40 transition-colors">
                        <td className="py-3 px-4 font-semibold text-gray-800">{r.tenant_name}</td>
                        <td className="py-3 px-4 text-xs text-gray-400">{r.partner_name || <span className="text-gray-300">Direct</span>}</td>
                        <td className="py-3 px-4"><Badge label={r.plan_name || ' -- '} color="bg-indigo-50 text-indigo-600" /></td>
                        <td className="py-3 px-4 font-mono text-xs text-gray-400">{fmt(r.plan_default_credits)} -</td>
                        <td className="py-3 px-4 font-bold text-gray-900">{fmt(r.allocated_credits)} - {r.is_override && <Badge label="Custom" color="bg-amber-50 text-amber-600" />}</td>
                        <td className="py-3 px-4 text-gray-500">{fmt(r.allocated_credits - r.available)} -</td>
                        <td className="py-3 px-4 font-semibold text-gray-800">{fmt(r.available)} -</td>
                        <td className="py-3 px-4 min-w-[140px]">
                          <div className="flex items-center gap-2">
                            <LinearBar pct={pct} height="h-2" />
                            <span className={`text-xs font-bold w-10 ${pc}`}>{pct.toFixed(0)}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-xs text-gray-400 whitespace-nowrap">{fmtD(r.expiry_date)}{r.is_recurring && ' >>'}</td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            {r.overdraft_allowed && <Badge label="OD" color="bg-orange-50 text-orange-600" />}
                            {r.alert_enabled && <Badge label="Alert" color="bg-sky-50 text-sky-600" />}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {drawer && (
        <AdminAllocationDrawer token={token}
          tenants={tenantsForDrawer}
          partners={partnersForDrawer}
          editItem={editItem}
          onClose={() => { setDrawer(false); setEditItem(null); }}
          onSuccess={() => { setDrawer(false); setEditItem(null); setFlash(editItem ? 'Allocation updated' : 'Allocation created'); load(); }} />
      )}
    </div>
  );
}
// -------------------------------------------------------------------------------
function PartnerCreditsView({ token, partnerId }) {
  const [tab, setTab] = useState('allocations');
  const [pool, setPool] = useState(null);
  const [tenants, setTns] = useState([]);
  const [allocs, setAllocs] = useState([]);
  const [overview, setOver] = useState([]);
  const [poolCfg, setPoolCfg] = useState({});
  const [alertOn, setAlertOn] = useState(true);
  const [alertPct, setAlertPct] = useState(80);
  const [alertSaving, setAlertSaving] = useState(false);
  const [alertFeedback, setAlertFeedback] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoad] = useState(true);
  const [flash, setFlash] = useState(null);

  // ── My Jobs sub-view state ──────────────────────────────────────────────
  const [usageView, setUsageView] = useState('overview'); // 'overview' | 'jobs'
  const [jobs, setJobs]     = useState([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsPage, setJobsPage] = useState(1);
  const jobsLimit = 20;
  const [jobBD, setJobBD]   = useState(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobFrom, setJobFrom] = useState('');
  const [jobTo, setJobTo]   = useState('');

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const [p, a, o] = await Promise.all([
        getPartnerCredits(token, partnerId),
        partnerListAllocations(token),
        partnerGetUsageOverview(token),
      ]);
      setPool(p); setTns(p?.tenants || []); setAllocs(Array.isArray(a) ? a : []);
      setOver(Array.isArray(o) ? o : []);
      // Load alert config separately — don't let missing DB columns break the main load
      try {
        const cfg = await getPartnerPoolConfig(token);
        setPoolCfg(cfg || {});
        setAlertOn(cfg?.alert_enabled !== false);
        setAlertPct(Number(cfg?.alert_threshold) || 80);
      } catch (cfgErr) {
        console.warn('Pool alert config unavailable (migration pending?):', cfgErr.message);
      }
    } catch (e) { console.error(e); } finally { setLoad(false); }
  }, [token, partnerId]);
  useEffect(() => { load(); }, [load]);

  const loadJobs = useCallback(async()=>{
   if ((jobFrom && !jobTo) || (!jobFrom && jobTo)) return; // Wait until both are set or both are empty
   setJobsLoading(true);
   try {
    const d = await partnerGetJobUsage(token, {fromDate:jobFrom, toDate:jobTo, page:jobsPage, limit:jobsLimit});
    setJobs(Array.isArray(d?.records)?d.records:[]);
    setJobsTotal(d?.total || 0);
    setJobBD(d?.breakdown||null);
   } catch(e){console.error(e);}
   finally{setJobsLoading(false);}
  },[token, jobFrom, jobTo, jobsPage]);

  // Auto-load jobs when sub-view is activated
  useEffect(()=>{ if(usageView==='jobs') loadJobs(); },[usageView, loadJobs]);
  useEffect(()=>{ setJobsPage(1); }, [jobFrom, jobTo]);

  if (loading) return <LoadingSpinner />;

  const total = pool?.pool_total || 0;
  const used = pool?.pool_allocated || 0;
  const avail = pool?.pool_available || (total - used);
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Pool health banner -- teal gradient */}
      <div className="bg-gradient-to-br from-teal-600 to-emerald-700 rounded-3xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-teal-200">Your Credit Pool</p>
            <p className="text-4xl font-black mt-1">{fmt(avail)} <span className="text-2xl font-normal text-teal-200">- available</span></p>
          </div>
          <div className="relative">
            <Arc pct={pct} size={100} stroke={10} />
            <div className="absolute inset-0 flex items-center justify-center flex-col">
              <span className="text-lg font-black">{pct.toFixed(0)}%</span>
              <span className="text-[10px] text-teal-200">used</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[{ label: 'Pool Total', val: `${fmt(total)} -` }, { label: 'Distributed', val: `${fmt(used)} -` }, { label: 'My Tenants', val: tenants.length }].map(c => (
            <div key={c.label} className="bg-white/10 backdrop-blur rounded-xl px-4 py-3">
              <p className="text-xs text-teal-200 font-semibold mb-1">{c.label}</p>
              <p className="text-xl font-black">{c.val}</p>
            </div>
          ))}
        </div>
        {avail < total * 0.1 && (
          <div className="mt-4 bg-amber-400/20 border border-amber-300/40 rounded-xl px-4 py-2 flex items-center gap-2 text-amber-100 text-sm">
            <DynamicIcon name="AlertTriangle" className="w-4 h-4 shrink-0" />Pool below 10% -- contact your platform admin for a top-up
          </div>
        )}
      </div>

      {/* ── Credit Pool Alerts ─ always visible, mirrors Tenant view ─────── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
              <DynamicIcon name="Bell" className="w-4 h-4 text-teal-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm">Credit Pool Alerts</p>
              <p className="text-xs text-gray-400">Get notified before your pool runs low</p>
            </div>
          </div>
          {/* Toggle */}
          <div
            role="switch" aria-checked={alertOn} tabIndex={0}
            className={`w-12 h-7 rounded-full relative cursor-pointer transition-colors shrink-0 ${alertOn ? 'bg-teal-500' : 'bg-gray-200'} ${alertSaving ? 'opacity-60 pointer-events-none' : ''}`}
            onClick={async () => {
              const next = !alertOn;
              setAlertOn(next);
              setAlertSaving(true);
              try {
                await updatePartnerPoolConfig(token, { alert_enabled: next, alert_threshold: alertPct });
                setAlertFeedback(next ? 'Alerts on' : 'Alerts off');
              } catch(e) { setAlertOn(!next); setAlertFeedback('Could not save'); }
              finally { setAlertSaving(false); setTimeout(() => setAlertFeedback(null), 3500); }
            }}
          >
            <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${alertOn ? 'left-6' : 'left-1'}`} />
          </div>
        </div>

        {alertOn && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-2">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <span className="text-xs text-gray-500 shrink-0">Alert when pool usage reaches</span>
              <div className="flex-1 relative min-w-0">
                <input type="range" min={10} max={100} step={5} value={alertPct}
                  onChange={e => setAlertPct(Number(e.target.value))}
                  className="w-full accent-teal-600" />
                <div className="relative h-5 mt-1">
                  {[10, 50, 70, 80, 90, 100].map(t => {
                    const pos = ((t - 10) / 90) * 100;
                    const style = t === 10 ? { left: '0%', transform: 'none' }
                      : t === 100 ? { left: '100%', transform: 'translateX(-100%)' }
                      : { left: `${pos}%`, transform: 'translateX(-50%)' };
                    return <span key={t} className="absolute text-[10px] text-gray-400 whitespace-nowrap select-none" style={style}>{t}%</span>;
                  })}
                </div>
              </div>
              <span className="text-xl font-black text-teal-600 w-14 text-center shrink-0">{alertPct}%</span>
            </div>
            <button type="button" disabled={alertSaving}
              onClick={async () => {
                setAlertSaving(true);
                try {
                  await updatePartnerPoolConfig(token, { alert_enabled: alertOn, alert_threshold: alertPct });
                  setAlertFeedback('Saved');
                } catch(e) { setAlertFeedback('Could not save'); }
                finally { setAlertSaving(false); setTimeout(() => setAlertFeedback(null), 4000); }
              }}
              className="px-4 py-1.5 bg-teal-600 text-white text-xs font-bold rounded-lg hover:bg-teal-700 disabled:opacity-50 shrink-0">
              {alertSaving ? '...' : 'Save threshold'}
            </button>
          </div>
        )}
        {!alertOn && <p className="text-xs text-gray-400">Pool alerts are off. Toggle on above to set a threshold.</p>}
        {poolCfg?._migration_pending && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
            <DynamicIcon name="AlertTriangle" className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-amber-800">DB migration required to save alerts</p>
              <p className="text-xs text-amber-600 mt-0.5">Run <code className="font-mono bg-amber-100 px-1 rounded">Section 9 of migrate_audit_columns.sql</code> on your database, then refresh.</p>
            </div>
          </div>
        )}
        {alertFeedback && (
          <p className={`text-xs font-medium mt-1 ${alertFeedback === 'Saved' || alertFeedback.includes('on') || alertFeedback.includes('off') ? 'text-emerald-600' : 'text-red-500'}`}>
            {alertFeedback}
          </p>
        )}
      </div>

      {/* ── Main Tabs ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-teal-50 rounded-xl p-1">
          {[
           { id: 'allocations', label: 'Allocations', icon: 'Receipt' },
           { id: 'tenants', label: 'Tenant Overview', icon: 'Users' },
           { id: 'usage', label: 'Usage', icon: 'BarChart3' }
          ].map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); if(t.id!=='usage') setUsageView('overview'); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === t.id ? 'bg-white dark:bg-gray-700 shadow text-teal-700 dark:text-teal-300' : 'text-teal-500 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-200'}`}>
              <DynamicIcon name={t.icon} className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>
        {(tab === 'allocations') && (
          <button onClick={() => { setEditItem(null); setDrawer(true); }} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-200">
            <DynamicIcon name="Plus" className="w-4 h-4" />Allocate to Tenant
          </button>
        )}
      </div>

      {/* ── Usage Sub-Toggle (visible only when Usage tab is active) ─── */}
      {tab==='usage'&&(
       <div className="flex items-center gap-2">
        <DynamicIcon name="Layers" className="w-4 h-4 text-gray-400"/>
        <span className="text-xs text-gray-400 font-medium mr-1">View:</span>
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
         {[
          {id:'overview', label:'Tenant Usage', icon:'Users'},
          {id:'jobs',     label:'My System Jobs', icon:'Activity'},
         ].map(v=>(
          <button key={v.id} id={`usage-subtab-${v.id}`}
           onClick={()=>setUsageView(v.id)}
           className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-all
            ${usageView===v.id
              ? 'bg-white dark:bg-gray-700 shadow text-teal-700 dark:text-teal-300'
              : 'text-gray-500 hover:text-gray-700'}`}>
           <DynamicIcon name={v.icon} className="w-4 h-4"/>
           {v.label}
          </button>
         ))}
        </div>
       </div>
      )}




      {flash && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3 rounded-xl flex justify-between"><span>'" {flash}</span><button onClick={() => setFlash(null)}>--</button></div>}

      {/* Allocations */}
      {tab === 'allocations' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-teal-50 border-b border-teal-100">
                <tr>{['Date', 'Tenant', 'Plan', 'Default', 'Allocated', 'Invoice', 'Period', 'Recurring', 'Status', ''].map(h =>
                  <th key={h} className="text-left text-xs font-bold text-teal-500 uppercase tracking-wide py-3 px-4 whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {allocs.length === 0 && <tr><td colSpan={9} className="text-center text-gray-300 py-12">No allocations yet</td></tr>}
                {allocs.map(r => (
                  <tr key={r.id} className="hover:bg-teal-50/30 transition-colors">
                    <td className="py-3 px-4 text-xs text-gray-400">{fmtD(r.created_at)}</td>
                    <td className="py-3 px-4 font-semibold text-gray-800">{r.tenant_name}</td>
                    <td className="py-3 px-4"><Badge label={r.plan_name || ' -- '} color="bg-teal-50 text-teal-700" /></td>
                    <td className="py-3 px-4 font-mono text-xs text-gray-400">{fmt(r.plan_default_credits)}</td>
                    <td className="py-3 px-4 font-bold text-gray-900">{fmt(r.credits)} - {r.is_override && <Badge label="Custom" color="bg-amber-100 text-amber-700" />}</td>
                    <td className="py-3 px-4 font-mono text-xs text-teal-600">{r.invoice_ref || <span className="text-gray-300"> -- </span>}</td>
                    <td className="py-3 px-4 text-xs text-gray-400">{fmtD(r.start_date)} -' {fmtD(r.expiry_date)}</td>
                    <td className="py-3 px-4">{r.is_recurring ? <Badge label=">>" color="bg-violet-100 text-violet-700" /> : <span className="text-gray-200"> -- </span>}</td>
                    <td className="py-3 px-4"><AllocationStatusBadge alloc={r} /></td>
                    <td className="py-3 px-4">
                      <button onClick={() => { setEditItem(r); setDrawer(true); }}
                        className="text-xs text-teal-600 hover:text-teal-800 opacity-0 group-hover:opacity-100 whitespace-nowrap font-medium">
                        Edit
                      </button>
                      <button onClick={() => { if(window.confirm('Delete allocation? This will reverse the credits.')) deleteAllocation(token, r.id).then(load); }}
                        className="text-xs text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 whitespace-nowrap font-medium ml-2">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tenant cards grid */}
      {tab === 'tenants' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-teal-700 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2">
            <DynamicIcon name="Info" className="w-3.5 h-3.5 shrink-0" />
            <p>Note: Tenant totals reflect their entire wallet balance, which includes credits allocated directly by the other admins alongside your partner allocations.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {overview.filter(r=>!r.is_partner_system).length === 0 && <p className="text-gray-300 col-span-3 text-center py-12">No tenants</p>}
            {overview.filter(r=>!r.is_partner_system).map(r => {
              const pct = r.used_pct || 0;
              const barColor = pct >= 90 ? 'bg-red-400' : pct >= 70 ? 'bg-amber-400' : 'bg-teal-400';
              return (
                <div key={r.tenant_id} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-bold text-gray-900">{r.tenant_name}</p>
                      <Badge label={r.plan_name || ' -- '} color="bg-teal-50 text-teal-600" />
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black text-gray-900">{fmt(r.available)}</p>
                      <p className="text-xs text-gray-400">- left</p>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>{fmt(r.allocated_credits - r.available)} used</span>
                      <span>{fmt(r.allocated_credits)} total {r.is_override && '(custom)'}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`${barColor} h-full rounded-full`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400 mt-3 pt-3 border-t border-gray-50">
                    <span>{r.invoice_ref && <span className="font-mono text-teal-600">{r.invoice_ref}</span>}</span>
                    <span>{fmtD(r.expiry_date)}{r.is_recurring && ' >>'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Usage tab -- partner system row + tenant rows */}
      {tab === 'usage' && (
        <div className="space-y-3">
          {/* ── My Jobs sub-view ─────────────────────────────────────────────── */}
          {usageView === 'jobs' && (
            <div className="space-y-4">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <DynamicIcon name="Calendar" className="w-4 h-4 text-gray-400" />
                  <input type="date" value={jobFrom} onChange={e => setJobFrom(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
                  <span className="text-gray-300 text-sm">→</span>
                  <input type="date" value={jobTo} onChange={e => setJobTo(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
                  <button onClick={() => { setJobFrom(''); setJobTo(''); }}
                    className="text-sm text-gray-400 hover:text-gray-600 px-2">Reset</button>
                </div>
                <button onClick={loadJobs}
                  className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700">
                  <DynamicIcon name="RefreshCw" className="w-3.5 h-3.5" />Refresh
                </button>
                <span className="text-xs text-gray-400 ml-auto">{jobs.length} records</span>
              </div>

              {/* Module breakdown bars */}
              {jobBD?.breakdown?.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                  <p className="text-sm font-bold text-gray-700 mb-4">Breakdown by Job Type</p>
                  <div className="space-y-3">
                    {jobBD.breakdown.map(b => (
                      <div key={b.module_type} className="flex items-center gap-4">
                        <div className={`w-20 text-center text-xs font-bold py-1 px-2 rounded-lg border ${MOD_CLR[b.module_type] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                          {b.module_type}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between text-xs text-gray-400 mb-1">
                            <span>{b.job_count} jobs · {b.total_duration_minutes}m</span>
                            <span className="font-semibold text-gray-700">{fmt(b.total_credits, 4)} ({b.percentage}%)</span>
                          </div>
                          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`${MOD_BAR[b.module_type] || 'bg-gray-400'} h-full rounded-full`} style={{ width: `${b.percentage || 0}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-50 flex justify-between text-sm">
                    <span className="text-gray-400">Total consumed</span>
                    <span className="font-black text-gray-900">{fmt(jobBD.total_credits, 4)}</span>
                  </div>
                </div>
              )}

              {/* Per-job table */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-50 flex items-center gap-2">
                  <DynamicIcon name="Activity" className="w-4 h-4 text-indigo-500" />
                  <p className="text-sm font-bold text-gray-700">Job Activity — Partner Admin (System)</p>
                </div>
                {jobsLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <DynamicIcon name="Loader2" className="w-6 h-6 text-indigo-400 animate-spin" />
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-indigo-50 border-b border-indigo-100">
                          <tr>{['Started', 'Module', 'Job Name / ID', 'Duration', 'Credits', 'Status'].map(h => (
                            <th key={h} className="text-left text-xs font-bold text-indigo-400 uppercase tracking-wide py-3 px-4 whitespace-nowrap">{h}</th>
                          ))}</tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {jobs.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-gray-300 py-14 text-sm">
                              No system jobs recorded yet
                            </td></tr>
                          )}
                          {jobs.slice(0, 100).map(r => (
                            <tr key={r.id || r.job_id} className="hover:bg-indigo-50/20 transition-colors">
                              <td className="py-2.5 px-4 text-xs text-gray-400 whitespace-nowrap">
                                {r.recorded_at || r.started_at ? formatDateTime(r.recorded_at || r.started_at) : '—'}
                              </td>
                              <td className="py-2.5 px-4">
                                <Badge label={r.module_type || '?'} color={MOD_CLR[r.module_type] || 'bg-gray-100 text-gray-600'} />
                              </td>
                              <td className="py-2.5 px-4 max-w-[160px] truncate" title={r.job_id}>
                                {r.job_name && <div className="text-gray-700 font-medium truncate" title={r.job_name}>{r.job_name}</div>}
                                <div className="font-mono text-[10px] text-gray-400 truncate">{r.job_id || '—'}</div>
                              </td>
                              <td className="py-2.5 px-4 text-gray-500">
                                {(Number(r.duration_seconds || 0) / 60).toFixed(2)}m
                              </td>
                              <td className="py-2.5 px-4 font-bold text-indigo-700">
                                {fmt(r.credits_consumed, 4)}
                              </td>
                              <td className="py-2.5 px-4">
                                <Badge label={r.status || '—'} color={STS_CLR[r.status] || 'bg-gray-100 text-gray-600'} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <PaginationControls page={jobsPage} total={jobsTotal} limit={jobsLimit} setPage={setJobsPage} />
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Tenant Overview sub-view ────────────────────────────────────── */}
          {usageView === 'overview' && (
            <div className="flex items-center gap-2 text-xs text-teal-700 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2">
              <DynamicIcon name="Info" className="w-3.5 h-3.5 shrink-0" />
              <p>The <strong>Partner Pool</strong> row shows your total credit budget from the platform admin, how much you've distributed to tenants, and what remains. Tenant rows show each tenant's individual wallet usage.</p>
            </div>
          )}

          {usageView === 'overview' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-teal-50 border-b border-teal-100">
                    <tr>{['', 'Tenant / Source', 'Plan', 'Allocated', 'Credit Used', 'Available', 'Usage %', 'Jobs', 'Expiry'].map(h =>
                      <th key={h} className="text-left text-xs font-bold text-teal-500 uppercase tracking-wide py-3 px-4 whitespace-nowrap">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {overview.length === 0 && <tr><td colSpan={9} className="text-center text-gray-300 py-12">No usage data</td></tr>}
                    {overview.map(r => {
                      const pct = r.used_pct || 0;
                      const c = pct >= 90 ? 'text-red-600' : pct >= 70 ? 'text-amber-600' : 'text-teal-600';

                      if (r.is_partner_system) {
                        // ── Partner Pool summary row ─────────────────────────────────────
                        // Read from `pool` state (pool_total / pool_allocated / pool_available)
                        // NOT from r.allocated_credits which is the pt_ billing wallet
                        // (always 0 when the partner hasn't run their own jobs).
                        const pAllocated = total;                        // credits given by platform admin
                        const pUsed      = used;                         // credits distributed to tenants
                        const pAvail     = avail;                        // credits not yet distributed
                        const pPct       = total > 0 ? Math.min(100, (pUsed / pAllocated) * 100) : 0;
                        const pColor     = pPct >= 90 ? 'text-red-600' : pPct >= 70 ? 'text-amber-600' : 'text-teal-600';
                        return (
                          <React.Fragment key={r.tenant_id}>
                            <tr className="bg-teal-50/60 border-l-4 border-teal-500">
                              <td className="py-3 px-4">
                                <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center">
                                  <DynamicIcon name="Wallet" className="w-3.5 h-3.5 text-teal-700" />
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <p className="font-bold text-teal-900">{r.tenant_name}</p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <Badge label="Partner Pool" color="bg-teal-100 text-teal-700" />
                                  <span className="text-[11px] text-teal-500">From platform admin</span>
                                </div>
                              </td>
                              <td className="py-3 px-4"><Badge label="Pool" color="bg-teal-100 text-teal-700" /></td>
                              {/* Allocated = total credits the platform admin gave this partner */}
                              <td className="py-3 px-4 font-bold text-gray-900">{fmt(pAllocated)}</td>
                              {/* Credit Used = credits distributed out to tenants */}
                              <td className="py-3 px-4">
                                <div>
                                  <span className="font-bold text-teal-700">{fmt(pUsed)}</span>
                                  <span className="text-[10px] text-gray-400 ml-1">(distributed to tenants)</span>
                                </div>
                              </td>
                              {/* Available = pool_total minus distributed */}
                              <td className="py-3 px-4 font-semibold text-gray-800">{fmt(pAvail)}</td>
                              <td className="py-3 px-4 min-w-[140px]">
                                <div className="flex items-center gap-2">
                                  <LinearBar pct={pPct} color="bg-teal-500" height="h-1.5" />
                                  <span className={`text-xs font-bold ${pColor}`}>{pPct.toFixed(1)}%</span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-xs text-gray-400">{tenants.length} tenant{tenants.length !== 1 ? 's' : ''}</td>
                              <td className="py-3 px-4 text-xs text-gray-400">—</td>
                            </tr>
                            {/* Module breakdown sub-rows (partner own system jobs) */}
                            {r.module_breakdown && r.module_breakdown.length > 0 && r.module_breakdown.map(m => (
                              <tr key={m.module_type} className="bg-teal-50/20">
                                <td className="py-2 px-4" />
                                <td className="py-2 px-4 pl-10">
                                  <span className="text-xs text-teal-600 flex items-center gap-1.5">
                                    <DynamicIcon name="CornerDownRight" className="w-3 h-3" />
                                    {m.module_type}
                                  </span>
                                </td>
                                <td colSpan={2} className="py-2 px-4" />
                                <td className="py-2 px-4 text-xs text-teal-700 font-mono">{fmt(m.credits, 4)}</td>
                                <td className="py-2 px-4" />
                                <td className="py-2 px-4 text-xs text-gray-400">{m.duration_minutes} min</td>
                                <td className="py-2 px-4 text-xs text-gray-500">{m.job_count} job{m.job_count !== 1 ? 's' : ''}</td>
                                <td className="py-2 px-4" />
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      }

                      // ── Regular tenant row ───────────────────────────────────────────
                      // Credit Used = wallet math (allocated - available): reliable even when
                      // usage_records hasn't been finalized yet for in-progress/new tenants.
                      const creditUsed = Number(r.allocated_credits || 0) - Number(r.available || 0);
                      return (
                        <tr key={r.tenant_id} className="hover:bg-teal-50/20 transition-colors">
                          <td className="py-3 px-4">
                            <div className="w-7 h-7 rounded-lg bg-teal-50 flex items-center justify-center">
                              <DynamicIcon name="Building2" className="w-3.5 h-3.5 text-teal-500" />
                            </div>
                          </td>
                          <td className="py-3 px-4 font-semibold text-gray-800">{r.tenant_name}</td>
                          <td className="py-3 px-4"><Badge label={r.plan_name || '—'} color="bg-teal-50 text-teal-600" /></td>
                          <td className="py-3 px-4 font-bold text-gray-900">
                            {fmt(r.allocated_credits)}{' '}
                            {r.is_override && <Badge label="Custom" color="bg-amber-50 text-amber-600" />}
                          </td>
                          {/* Credit Used = allocated minus current wallet balance */}
                          <td className="py-3 px-4">
                            {creditUsed > 0 ? (
                              <span className="font-bold text-indigo-700">{fmt(creditUsed, 4)}</span>
                            ) : (
                              <span className="text-gray-300 text-xs">0.0000</span>
                            )}
                          </td>
                          <td className="py-3 px-4 font-semibold text-gray-800">{fmt(r.available)}</td>
                          <td className="py-3 px-4 min-w-[120px]">
                            <div className="flex items-center gap-2">
                              <LinearBar pct={pct} color="bg-teal-500" height="h-1.5" />
                              <span className={`text-xs font-bold ${c}`}>{pct.toFixed(0)}%</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-xs text-gray-400">{fmtD(r.expiry_date)}{r.is_recurring && ' »'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {drawer && (
        <AllocationDrawer token={token} tenants={tenants.map(t => ({ tenant_id: t.tenant_id, tenant_name: t.tenant_name }))}
          partnerId={partnerId} poolAvailable={avail} editItem={editItem}
          onClose={() => { setDrawer(false); setEditItem(null); }} onSuccess={() => { setDrawer(false); setEditItem(null); setFlash(editItem ? 'Allocation updated' : 'Credits allocated'); load(); }} />
      )}
    </div>
  );
}

// -------------------------------------------------------------------------------
// TENANT VIEW -- Personal dashboard: purple/violet, consumer-friendly, arc meter
// -------------------------------------------------------------------------------
function TenantCreditsView({ token }) {
  const [tab, setTab] = useState('billing');
  const [wallet, setWallet] = useState(null);
  const [alloc, setAlloc] = useState(null);
  const [txns, setTxns] = useState([]);
  const [allocHistory, setAllocHistory] = useState([]);
  const [breakdown, setBD] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [rowsPage, setRowsPage] = useState(1);
  const rowsLimit = 20;
  const [dim, setDim] = useState('activity');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [alertOn, setAlertOn] = useState(true);
  const tenantBranding = useTenantExportBranding();
  const [alertPct, setAP] = useState(80);
  const [saving, setSaving] = useState(false);
  const [alertSaveFeedback, setAlertSaveFeedback] = useState(null);

  const load = useCallback(async () => {
    if ((from && !to) || (!from && to)) return; // Wait until both are set or both are empty
    setLoading(true);
    try {
      const [w, a, t, allocHist, b] = await Promise.all([
        getMyWallet(token), getMyAllocation(token), getMyTransactions(token, 1, 10),
        getMyAllocationHistory(token),
        getUsageBreakdown(token, from, to),
      ]);
      setWallet(w); setAlloc(a || null);
      setTxns(Array.isArray(t?.transactions) ? t.transactions : []);
      setAllocHistory(Array.isArray(allocHist) ? allocHist : []);
      setBD(b);
      setAlertOn(w?.alert_enabled !== false); setAP(w?.alert_threshold || 80);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, from, to]);
  useEffect(() => { load(); }, [load]);

  const loadUsage = useCallback(async () => {
    if ((from && !to) || (!from && to)) return;
    try {
      const u = await getMyUsageFiltered(token, { fromDate: from, toDate: to, page: rowsPage, limit: rowsLimit });
      setRows(Array.isArray(u?.records) ? u.records : Array.isArray(u) ? u : []);
      setRowsTotal(u?.total || 0);
    } catch (e) { console.error(e); }
  }, [token, from, to, rowsPage]);

  useEffect(() => { loadUsage(); }, [loadUsage]);
  useEffect(() => { setRowsPage(1); }, [from, to]);

  /** Persist alert_enabled + alert_threshold; syncs wallet from server. */
  const persistAlerts = async (enabled, threshold) => {
    setSaving(true);
    setAlertSaveFeedback(null);
    try {
      await updateMyAlertSettings(token, { alert_enabled: enabled, alert_threshold: threshold });
      const w = await getMyWallet(token);
      setWallet(w);
      setAlertOn(w?.alert_enabled !== false);
      setAP(Number(w?.alert_threshold) || 80);
      setAlertSaveFeedback('Saved');
      setTimeout(() => setAlertSaveFeedback(null), 4000);
    } catch (e) {
      setAlertSaveFeedback(e.message || 'Could not save alert settings');
    } finally {
      setSaving(false);
    }
  };

  const saveAlert = async () => {
    await persistAlerts(alertOn, alertPct);
  };

  /** Toggle must persist immediately — when off, the threshold row (and old Save) was hidden so OFF could never be saved. */
  const toggleAlertsEnabled = async () => {
    const next = !alertOn;
    setAlertOn(next);
    setSaving(true);
    setAlertSaveFeedback(null);
    try {
      await updateMyAlertSettings(token, { alert_enabled: next, alert_threshold: alertPct });
      const w = await getMyWallet(token);
      setWallet(w);
      setAlertOn(w?.alert_enabled !== false);
      setAP(Number(w?.alert_threshold) || 80);
      setAlertSaveFeedback(next ? 'Alerts on' : 'Alerts off');
      setTimeout(() => setAlertSaveFeedback(null), 3500);
    } catch (e) {
      setAlertOn(!next);
      setAlertSaveFeedback(e.message || 'Could not update alerts');
    } finally {
      setSaving(false);
    }
  };

  const byDay = useMemo(() => {
    if (breakdown?.by_day) return breakdown.by_day;
    // Fallback if backend hasn't updated yet or missing
    const m = {};
    rows.forEach(r => {
      const d = (r.recorded_at || r.started_at || '').slice(0, 10); if (!d) return;
      if (!m[d]) m[d] = { date: d, jobs: 0, credits: 0, mods: {} };
      m[d].jobs++; m[d].credits += Number(r.credits_consumed || 0);
      const k = r.module_type || '?'; m[d].mods[k] = (m[d].mods[k] || 0) + 1;
    });
    return Object.values(m).sort((a, b) => b.date.localeCompare(a.date));
  }, [breakdown?.by_day, rows]);
  const maxDay = Math.max(...byDay.map(d => d.credits), 1);

  if (loading) return <LoadingSpinner />;

  const bal = wallet?.balance || 0;
  const alloc_cr = wallet?.monthly_allocation || 0;
  const res = wallet?.reserved || 0;
  const avail = wallet?.available ?? (bal - res);
  const used = alloc_cr > 0 ? alloc_cr - avail : 0;
  const pct = alloc_cr > 0 ? Math.min(100, (used / alloc_cr) * 100) : 0;
  const pctColor = pct >= 90 ? 'text-red-500' : pct >= 70 ? 'text-amber-500' : 'text-violet-600';

  return (
    <div className="space-y-6">
      {/* Zero-credit notice -- shown when wallet has no credits and no active allocation */}
      {bal === 0 && !alloc && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl px-5 py-4 flex items-start gap-4">
          <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
            <DynamicIcon name="AlertTriangle" className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <p className="text-sm font-bold text-amber-800">No credits allocated yet</p>
            <p className="text-xs text-amber-600 mt-0.5">
              Your account has 0 credits. Please contact your{" "}
              <strong>Platform Admin</strong> to allocate a credit plan.
            </p>
          </div>
        </div>
      )}
      {/* Hero balance card -- violet/purple gradient */}
      <div className="bg-gradient-to-br from-violet-600 to-indigo-700 rounded-3xl p-6 text-white shadow-xl">
        <div className="flex items-center gap-6">
          {/* Arc meter */}
          <div className="relative shrink-0">
            <Arc pct={pct} size={130} stroke={13} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-2xl font-black ${pct >= 90 ? 'text-red-300' : pct >= 70 ? 'text-amber-300' : 'text-white'}`}>{pct.toFixed(0)}%</span>
              <span className="text-[11px] text-violet-200 font-semibold">used</span>
            </div>
          </div>
          {/* Balance info */}
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-widest text-violet-200 mb-1">Available Credits</p>
            <p className="text-5xl font-black">{fmt(avail)} <span className="text-2xl font-normal text-violet-200">-</span></p>
            <div className="flex flex-wrap gap-2 mt-3">
              {alloc?.plan_name && <span className="bg-white/15 text-white text-xs font-semibold px-3 py-1 rounded-full">{alloc.plan_name}</span>}
              {alloc?.is_recurring && <span className="bg-white/15 text-white text-xs font-semibold px-3 py-1 rounded-full">&raquo; Recurring</span>}
              {alloc?.is_override && <span className="bg-amber-400/30 text-amber-100 text-xs font-semibold px-3 py-1 rounded-full border border-amber-300/30">Custom Quota</span>}
            </div>
          </div>
          {/* Quick stats */}
          <div className="shrink-0 grid grid-cols-2 gap-3 text-center">
            {[
              { label: 'Allocated', val: `${fmt(alloc_cr)} -` },
              { label: 'Used', val: `${fmt(used)} -` },
              { label: 'Reserved', val: `${fmt(res)} -` },
              { label: 'Resets', val: alloc?.expiry_date ? formatDateTime(alloc.expiry_date) : ' -- ' },
            ].map(s => (
              <div key={s.label} className="bg-white/10 rounded-xl px-3 py-2">
                <p className="text-xs text-violet-200">{s.label}</p>
                <p className="text-sm font-black">{s.val}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Plan default vs allocated */}
        {alloc && (
          <div className="mt-5 border-t border-white/10 pt-4 flex flex-wrap items-center gap-4 text-sm">
            <span className="text-violet-200">Plan default: <strong className="text-white">{fmt(alloc.plan_default_credits)} -/mo</strong></span>
            {alloc.invoice_ref && <span className="text-violet-200">Invoice: <strong className="font-mono text-white">{alloc.invoice_ref}</strong></span>}
            {alloc.expiry_date && <span className="text-violet-200">Expires: <strong className="text-white">{fmtD(alloc.expiry_date)}</strong></span>}
          </div>
        )}
      </div>

      {/* Alert settings card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
              <DynamicIcon name="Bell" className="w-4 h-4 text-violet-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm">Credit Usage Alerts</p>
              <p className="text-xs text-gray-400">Get notified before you run out</p>
            </div>
          </div>
          <div
            role="switch"
            aria-checked={alertOn}
            tabIndex={0}
            className={`w-12 h-7 rounded-full relative cursor-pointer transition-colors shrink-0 ${alertOn ? 'bg-violet-500' : 'bg-gray-200'} ${saving ? 'opacity-60 pointer-events-none' : ''}`}
            onClick={() => { if (!saving) toggleAlertsEnabled(); }}
            onKeyDown={(e) => {
              if (saving) return;
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleAlertsEnabled(); }
            }}
          >
            <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${alertOn ? 'left-6' : 'left-1'}`} />
          </div>
        </div>
        {alertOn && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-3">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <span className="text-xs text-gray-500 shrink-0">Alert when usage reaches</span>
              <div className="flex-1 relative min-w-0">
                <input
                  type="range"
                  min={CREDIT_ALERT_SLIDER_MIN}
                  max={CREDIT_ALERT_SLIDER_MAX}
                  step="5"
                  value={alertPct}
                  onChange={e => setAP(Number(e.target.value))}
                  className="w-full accent-violet-600"
                />
                <div className="relative h-5 mt-1">
                  {CREDIT_ALERT_SLIDER_TICKS.map((t) => {
                    const p = creditAlertValueToTrackPct(t);
                    const style =
                      t === CREDIT_ALERT_SLIDER_MIN
                        ? { left: '0%', transform: 'none' }
                        : t === CREDIT_ALERT_SLIDER_MAX
                          ? { left: '100%', transform: 'translateX(-100%)' }
                          : { left: `${p}%`, transform: 'translateX(-50%)' };
                    return (
                      <span
                        key={t}
                        className="absolute text-[10px] text-gray-400 whitespace-nowrap select-none"
                        style={style}
                      >
                        {t}%
                      </span>
                    );
                  })}
                </div>
              </div>
              <span className="text-xl font-black text-violet-600 w-14 text-center shrink-0">{alertPct}%</span>
            </div>
            <div className="flex items-center gap-2 shrink-0 justify-end sm:justify-start">
              <button type="button" onClick={saveAlert} disabled={saving}
                className="px-4 py-1.5 bg-violet-600 text-white text-xs font-bold rounded-lg hover:bg-violet-700 disabled:opacity-50">
                {saving ? '...' : 'Save threshold'}
              </button>
            </div>
          </div>
        )}
        {!alertOn && (
          <p className="text-xs text-gray-500 mb-2">Usage alerts are off. Turn the switch on above to choose a threshold.</p>
        )}
        {alertSaveFeedback && (
          <div className={`text-xs font-medium ${alertSaveFeedback === 'Saved' || alertSaveFeedback === 'Alerts on' || alertSaveFeedback === 'Alerts off' ? 'text-emerald-600' : 'text-red-600'}`}>
            {alertSaveFeedback}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-violet-50 rounded-xl p-1">
        {[{ id: 'billing', label: 'Billing History', icon: 'Receipt' }, { id: 'usage', label: 'Usage', icon: 'BarChart3' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${tab === t.id ? 'bg-white dark:bg-gray-700 shadow text-violet-700 dark:text-violet-300' : 'text-violet-400 dark:text-violet-500 hover:text-violet-600 dark:hover:text-violet-300'}`}>
            <DynamicIcon name={t.icon} className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {/* Billing History -- allocation-only view with invoice details */}
      {tab === 'billing' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
            <p className="text-sm font-bold text-gray-700">Credit Allocations</p>
            <p className="text-xs text-gray-400">Credits allocated by your admin</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-violet-50 border-b border-violet-100">
                <tr>{['Date', 'Plan', 'Credits', 'Invoice Ref', 'Period', 'Recurring', 'Status'].map(h =>
                  <th key={h} className="text-left text-xs font-bold text-violet-400 uppercase tracking-wide py-3 px-5">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {allocHistory.length === 0 &&
                  <tr><td colSpan={7} className="text-center text-gray-300 py-12">No credit allocations yet</td></tr>}
                {allocHistory.map((h, i) => {
                  return (
                  <tr key={i} className="hover:bg-violet-50/30 transition-colors">
                    <td className="py-3 px-5 text-xs text-gray-400 whitespace-nowrap">{fmtD(h.created_at)}</td>
                    <td className="py-3 px-5"><Badge label={h.plan_name || '--'} color="bg-violet-50 text-violet-700" /></td>
                    <td className="py-3 px-5 font-bold text-emerald-600">+{fmt(h.credits, 0)} credits</td>
                    <td className="py-3 px-5 font-mono text-xs text-indigo-600 font-semibold">{h.invoice_ref || <span className="text-gray-300">--</span>}</td>
                    <td className="py-3 px-5 text-xs text-gray-400 whitespace-nowrap">
                      {h.start_date && h.expiry_date ? `${fmtD(h.start_date)} to ${fmtD(h.expiry_date)}` : '--'}
                    </td>
                    <td className="py-3 px-5">
                      {h.is_recurring
                        ? <Badge label="Monthly" color="bg-violet-100 text-violet-700" />
                        : <span className="text-gray-300 text-xs">One-time</span>}
                    </td>
                    <td className="py-3 px-5">
                      <AllocationStatusBadge alloc={h} />
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Usage tab */}
      {tab === 'usage' && (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-1 bg-violet-50 rounded-xl p-1">
              {[{ id: 'activity', label: 'By Activity' }, { id: 'day', label: 'By Day' }, { id: 'module', label: 'By Module' }].map(d => (
                <button key={d.id} onClick={() => setDim(d.id)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${dim === d.id ? 'bg-white dark:bg-gray-700 shadow text-violet-700 dark:text-violet-300' : 'text-violet-400 dark:text-violet-500 hover:text-violet-600 dark:hover:text-violet-300'}`}>
                  {d.label}
                </button>
              ))}
            </div>
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); }} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
            <input type="date" value={to} onChange={e => { setTo(e.target.value); }} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
            <button onClick={() => { setFrom(''); setTo(''); }} className="text-sm text-gray-400 hover:text-gray-600 px-2">Reset</button>

          </div>

          {/* By Module */}
          {dim === 'module' && breakdown?.breakdown?.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
              <p className="text-sm font-bold text-gray-700 mb-5">Usage Breakdown by Module</p>
              <div className="space-y-4">
                {breakdown.breakdown.map(b => (
                  <div key={b.module_type} className="flex items-center gap-4">
                    <div className={`w-24 text-center text-xs font-bold py-1 px-2 rounded-lg border ${MOD_CLR[b.module_type] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                      {b.module_type}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-xs text-gray-400 mb-1">
                        <span>{b.job_count} jobs . {b.total_duration_minutes}m</span>
                        <span className="font-semibold text-gray-700">{fmt(b.total_credits, 2)} - ({b.percentage}%)</span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`${MOD_BAR[b.module_type] || 'bg-gray-400'} h-full rounded-full`} style={{ width: `${b.percentage || 0}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-gray-50 flex justify-between text-sm">
                <span className="text-gray-400">Total</span>
                <span className="font-black text-gray-900">{fmt(breakdown.total_credits, 2)} -</span>
              </div>
            </div>
          )}

          {/* By Activity */}
          {dim === 'activity' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-violet-50 border-b border-violet-100">
                    <tr>{['Started', 'Module', 'Job Name / ID', 'Duration', 'Credits', 'Status'].map(h =>
                      <th key={h} className="text-left text-xs font-bold text-violet-400 uppercase tracking-wide py-3 px-4 whitespace-nowrap">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.length === 0 && <tr><td colSpan={6} className="text-center text-gray-300 py-12">No usage records in this period</td></tr>}
                    {rows.slice(0, 100).map(r => (
                      <tr key={r.id || r.job_id} className="hover:bg-violet-50/20 transition-colors">
                        <td className="py-2.5 px-4 text-xs text-gray-400 whitespace-nowrap">{(r.started_at || r.recorded_at) ? formatDateTime(r.started_at || r.recorded_at) : ' -- '}</td>
                        <td className="py-2.5 px-4"><Badge label={r.module_type || '?'} color={MOD_CLR[r.module_type] || 'bg-gray-100 text-gray-600'} /></td>
                        <td className="py-2.5 px-4 max-w-[160px] truncate" title={r.job_id}>
                          {r.job_name && <div className="text-gray-700 font-medium truncate" title={r.job_name}>{r.job_name}</div>}
                          <div className="font-mono text-[10px] text-gray-400 truncate">{r.job_id || ' -- '}</div>
                        </td>
                        <td className="py-2.5 px-4 text-gray-500">{(Number(r.duration_seconds || 0) / 60).toFixed(1)}m</td>
                        <td className="py-2.5 px-4 font-bold text-gray-900">{fmt(r.credits_consumed, 4)} -</td>
                        <td className="py-2.5 px-4"><Badge label={r.status || ' -- '} color={STS_CLR[r.status] || 'bg-gray-100 text-gray-600'} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationControls page={rowsPage} total={rowsTotal} limit={rowsLimit} setPage={setRowsPage} />
            </div>
          )}

          {/* By Day */}
          {dim === 'day' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-violet-50 border-b border-violet-100">
                    <tr>{['Date', 'Jobs', 'Credits Used', 'Modules', 'Trend'].map(h =>
                      <th key={h} className="text-left text-xs font-bold text-violet-400 uppercase tracking-wide py-3 px-4">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {byDay.length === 0 && <tr><td colSpan={5} className="text-center text-gray-300 py-12">No usage in this period</td></tr>}
                    {byDay.map(d => (
                      <tr key={d.date} className="hover:bg-violet-50/20 transition-colors">
                        <td className="py-3 px-4 font-semibold text-gray-700 whitespace-nowrap">
                          {formatDateTime(d.date)}
                        </td>
                        <td className="py-3 px-4 text-gray-500">{d.jobs}</td>
                        <td className="py-3 px-4 font-bold text-gray-900">{fmt(d.credits, 2)} -</td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(d.mods).map(([m, c]) => (
                              <Badge key={m} label={`${m} --${c}`} color={MOD_CLR[m] || 'bg-gray-100 text-gray-600'} />
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4 min-w-[120px]">
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="bg-violet-400 h-full rounded-full" style={{ width: `${(d.credits / maxDay) * 100}%` }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------------
// ROOT -- Role router
// -------------------------------------------------------------------------------
export default function CreditsPage() {
  const { user, token } = useAuth();
  // roleScope comes from r.scope in /users/me -' "Platform" | "Partner" | "Tenant"
  // roleId: r1=Super Admin, r2=Platform Admin, r3=Partner Admin, r4/r7=Tenant
  const roleScope = user?.roleScope || '';
  const roleId = user?.roleId || '';
  const isAdmin = roleScope === 'Platform';     // r1 + r2
  const isPartner = roleScope === 'Partner';      // r3
  // After schema migration: users.partnerId column was dropped.
  // Partner Admin's tenantId IS their partner tenant id (tenant_type='PARTNER').
  const rawTid = user?.tenantId || null;
  const partnerId = isPartner ? rawTid : null;

  return (
    <StandardPageLayout>
      <div className="mt-6">
        {isAdmin && <AdminCreditsView token={token} />}
        {isPartner && <PartnerCreditsView token={token} partnerId={partnerId} />}
        {!isAdmin && !isPartner && <TenantCreditsView token={token} />}
      </div>
    </StandardPageLayout>
  );
}
